import React, { useState, useEffect, useRef } from "react";
import axios from "../../../../../axios-config.js";
import { connect } from "react-redux";

function ImagePanel(props) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <div
        className="EditorDrawerItemHeader"
        onClick={() => setOpen(!open)}
      >
        <span className="PanelOpenStatus">{open ? "▾" : "▸"}</span>
        <span>Image</span>
      </div>
      {open && (
        <ImageDrawer
          url={props.url}
          onImageLoad={props.onImageLoad}
          activeProjectID={props.activeProjectID}
          {...props}
        />
      )}
    </div>
  );
}

function ImageDrawer(props) {
  const [isLoading, setIsLoading] = useState(false);
  const [originalFileName, setOriginalFileName] = useState("");
  const [showFileName, setShowFileName] = useState(false);
  const [isFileDialogOpen, setIsFileDialogOpen] = useState(false);

  const fileInputRef = useRef(null);
  const supportsStaticImageVisibility = props.type === "STATIC_IMAGE" || props.type === "STATIC_IMAGE_LABEL";
  const imageVisible = props.imageVisible !== false;
  const hasImage = Boolean(props.url);

  useEffect(() => {
    if (props.url) {
      const assetIdentifier = props.url.split("/").pop();
      setIsLoading(true);
      axios
        .get(`/upload/original-filename/${assetIdentifier}`)
        .then((response) => {
          setOriginalFileName(response.data.originalFileName);
          setShowFileName(response.data.originalFileName !== "");
        })
        .catch((error) => {
          console.error("Error fetching original filename:", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setOriginalFileName("");
      setShowFileName(false);
      setIsLoading(false);
    }
  }, [props.url]);

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setShowFileName(false);
      setIsLoading(true);
      setOriginalFileName(file.name);
      const form = new FormData();
      form.append("asset", file);
      form.append("projectID", props.activeProjectID);
      axios
        .post("/upload/", form)
        .then((response) => {
          if (response.data.url) {
            props.onImageLoad(response.data.url);
          }
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Upload error:", error);
          setIsLoading(false);
        });
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setIsFileDialogOpen(false);
  };

  const handleUploadClick = (e) => {
    e.stopPropagation();
    if (!isFileDialogOpen) {
      setIsFileDialogOpen(true);
      fileInputRef.current?.click();
    }
  };

  const handleToggleImageVisible = (e) => {
    e.stopPropagation();
    if (
      !supportsStaticImageVisibility ||
      typeof Inventum === "undefined" ||
      !Inventum.labels ||
      typeof Inventum.labels.toggleStaticImageVisible !== "function"
    ) return;

    Inventum.labels.toggleStaticImageVisible(props.id);
    props.getLabels?.();
  };

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setOriginalFileName("");
    setShowFileName(false);
    props.onImageLoad("");
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(originalFileName).catch((error) => {
      console.error("Copy to clipboard failed:", error);
    });
  };

  return (
    <div className="ImageDrawerContainer">
      {isLoading && (
        <div className="ImageLoadingContainer">
          <div className="ImageLoadingSpinner"></div>
        </div>
      )}

      {!isLoading && props.url && (
        <div
          className="ImagePreview"
          style={{ backgroundImage: `url(${props.url})` }}
        />
      )}

      <div className="ImageActions">
        <div className="ImageActionsLeft">
          <div className="ImagePrimaryActions">
            <input
              className="ImageFileInput"
              accept="image/*"
              type="file"
              onChange={onFileChange}
              ref={fileInputRef}
            />
            <button
              className="ImageUploadButton"
              onClick={handleUploadClick}
              disabled={isLoading}
              title="Upload image"
              aria-label="Upload image"
            >
              <span className="material-icons">upload_file</span>
              Upload
            </button>

            {supportsStaticImageVisibility && hasImage && (
              <button
                className={"ImageVisibilityButton" + (imageVisible ? " active" : " inactive")}
                onClick={handleToggleImageVisible}
                disabled={isLoading}
                title={imageVisible ? "Hide image but keep text labels visible" : "Show image"}
                aria-label={imageVisible ? "Hide image" : "Show image"}
              >
                <span className="material-icons">{imageVisible ? "image" : "hide_image"}</span>
                {imageVisible ? "Hide" : "Show"}
              </button>
            )}

            {hasImage && (
              <button
                className="ImageDeleteButton"
                onClick={handleRemoveImage}
                disabled={isLoading}
                title="Delete image from this label"
                aria-label="Delete image from this label"
              >
                <span className="material-icons">delete</span>
                Delete
              </button>
            )}
          </div>

          {originalFileName && (
            <button
              className="ImageToggleButton"
              onClick={() => setShowFileName(!showFileName)}
            >
              <span className="material-icons">
                {showFileName ? 'visibility_off' : 'visibility'}
              </span>
              {showFileName ? "Hide Filename" : "Show Filename"}
            </button>
          )}
        </div>

      </div>

      {showFileName && originalFileName && (
        <div className="ImageFilename">
          <span className="ImageFilenameText">{originalFileName}</span>
          <button
            className="ImageCopyButton"
            onClick={handleCopyToClipboard}
            title="Copy to clipboard"
          >
            <span className="material-icons">content_copy</span>
          </button>
        </div>
      )}
    </div>
  );
}

// Redux mapping
const mapStateToProps = (state) => ({
  activeProjectID: state.ui.activeProject,
});

// If you don't need to dispatch anything, you can omit mapDispatchToProps
const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(ImagePanel);
